import { browser } from '$app/env';
import { localStorageSupported } from '#lib/helper/browser.js';
import type { AppLocale } from '#lib/helper/locale.js';
import { businessStore, createDefaultBusiness } from '#lib/stores/business.js';
import { selectedLocale } from '#lib/stores/i18n.js';
import { type User, UserRole, apiUserLocale, apiUserMe } from '#lib/types/generated/index.js';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';

export const AUTH_USER_ID_STORAGE_KEY = 'auth_user_id';

export function createDefaultUser(): User {
	return {
		id: '',
		email: '',
		firstName: '',
		lastName: '',
		phone: '',
		role: UserRole.User,
		locale: 'fr',
		defaultBusinessId: '',
		releaseNotesReadAt: DateTime.now(),
		doNotDisturbUntil: null,
		cguAcceptedAt: null,
		termsAcceptedAt: null,
		privacyAcceptedAt: null
	};
}

export const userStore = writable<User>(createDefaultUser());

selectedLocale.subscribe((locale) => {
	const user = get(userStore);
	if (user.id && user.locale !== locale) {
		apiUserLocale({ locale });

		// Proactively update the user model locale
		userStore.update((u) => ({
			...u,
			locale
		}));
	}
});

if (browser) {
	window.addEventListener('storage', (e) => {
		if (e.key === AUTH_USER_ID_STORAGE_KEY && e.newValue && e.newValue !== get(userStore).id) {
			void loadUser().catch(() => {});
		}
	});
}

export async function loadUser(): Promise<void> {
	try {
		const previousUserId = get(userStore).id;
		const user = await apiUserMe();
		if (previousUserId && previousUserId !== user.id) {
			businessStore.set(createDefaultBusiness());
		}
		userStore.set(user);
		selectedLocale.set(user.locale as AppLocale);
		if (localStorageSupported() && user.id) {
			localStorage.setItem(AUTH_USER_ID_STORAGE_KEY, user.id);
		}
	} catch {
		userStore.set(createDefaultUser());
		throw new Error('Not connected');
	}
}
